import React, {ChangeEventHandler, useEffect, useState} from 'react';
import {browserHistory} from 'react-router';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import {makeStyles} from '@material-ui/core/styles';
import {lightTheme} from '@components/@theme/lightTheme';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import {Lock, Visibility, VisibilityOff} from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import 'styles/WelcomePage.styl';
import 'styles/Login.styl';
import {
  getStrengthClassName,
  isPasswordStrengthSufficient,
  strength,
} from '@components/password/utils';
import {PasswordSource} from '@store/password/PasswordSource';

interface PasswordWelcomeFormProps {
  code: string;
  firstName: string;
}

export const PasswordWelcomeForm: React.FC<PasswordWelcomeFormProps> = ({code, firstName}) => {
  const [passwordStrength, setPasswordStrength] = useState<number | undefined>(0);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  // Estimate password strength.
  useEffect(() => {
    setPasswordStrength(strength(password));
  }, [password]);

  // If the user's typed something, change the style.
  const passwordDisplayStyle =
    password.length > 0 || passwordConfirm.length > 0 ? {marginBottom: '20px'} : {display: 'none'};
  // Figure out what the strength meter should look like.
  const strengthMeterClassName = classNames('meter', getStrengthClassName(passwordStrength));

  // Tell the user whether the passwords match.
  const passwordsMatch = password.length > 0 && password === passwordConfirm;
  const passwordMatchStatus = passwordsMatch ? 'Yes' : 'No';
  const passwordMatchClassName = classNames(passwordsMatch ? 'matched' : 'no-match');

  const valid = passwordsMatch && isPasswordStrengthSufficient(passwordStrength);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    // Don't submit if not valid.
    if (valid) {
      const onSuccess = () => {
        setSuccess(true);
        setError(false);
        // redirect to 'login', with enough time to see success message
        setTimeout(() => {
          browserHistory.replace('/login');
        }, 2000);
      };

      const onError = () => {
        setSuccess(false);
        setError(true);
      };

      PasswordSource.resetPasswordWithCode(code, password, passwordConfirm)
        .then(onSuccess)
        .catch(onError);
    }
  };

  return (
    <div className="welcome-page">
      <div className="centered-content">
        <div className="card-container">
          <div className="card">
            <div className="title">
              <h3>{firstName}, Welcome to Coach!</h3>
              <p>To get started, please choose a password.</p>
            </div>
            <div className="body">
              <div className="login-component-container">
                <div className="login-component">
                  <PasswordInput
                    onChange={e => setPassword(e.target.value)}
                    value={password}
                    label="New Password"
                  />
                  <PasswordInput
                    onChange={e => setPasswordConfirm(e.target.value)}
                    value={passwordConfirm}
                    label="Repeat New Password"
                  />
                  <div className="password-strength" style={passwordDisplayStyle}>
                    <div className="label">Password strength:</div>
                    <div className={strengthMeterClassName}></div>
                  </div>
                  <div className="password-match" style={passwordDisplayStyle}>
                    <div className="label">Password match:</div>
                    <div className="match">
                      <span className={passwordMatchClassName}>{passwordMatchStatus}</span>
                    </div>
                  </div>
                  <Button
                    disabled={!valid}
                    size="large"
                    color="primary"
                    variant="contained"
                    onClick={submit}>
                    Confirm
                  </Button>
                  {success && (
                    <div className="form-text message">Your password was successfully updated.</div>
                  )}
                  {error && (
                    <div className="form-text message">
                      There was an error updating your password.
                    </div>
                  )}
                </div>
              </div>
              <div className="help-text">
                <p>
                  Your Coach password needs to meet the high strength requirements created by NIST.
                </p>
                <p>
                  To create a strong password, choose a short phrase that is memorable to you, and
                  modify it by changing letter case or swapping characters for numbers or symbols
                  until the password strength bar goes green.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PasswordInputProps {
  label: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  value: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  onChange,
  value,
}: PasswordInputProps) => {
  const {password} = useStyles(lightTheme);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <TextField
      className={password}
      onChange={onChange}
      variant="outlined"
      value={value}
      label={label}
      type={showPassword ? 'text' : 'password'}
      autoComplete="current-password"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Lock color="disabled" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}>
              <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <Visibility /> : <VisibilityOff color="disabled" />}
              </Tooltip>
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const useStyles = makeStyles(() => ({
  password: {
    height: 48,
    display: 'flex',
    fontSize: '18px',
  },
}));
